import React, { useRef, useEffect, useCallback } from 'react'
import { Box, Typography, Grid2 as Grid, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import * as cornerstoneTools from '@cornerstonejs/tools'

// Hooks
import { useGetTimePointSubmissionDetails } from '@features/subject/hooks/useGetTimePointSubmissionDetails'
import { useGetPHIRedactionStatus } from '@features/subject/hooks/useGetPHIRedactionStatus'
import { useUpdatePHIRedactionBoxes } from '@features/subject/hooks/useUpdatePHIRedactionBoxes'
import { usePHIRedactionContext, PHIRedactionProvider } from './context/PHIRedactionContext'
import { useImageViewer } from './hooks/useImageViewer'
import { usePHIProcessing } from './hooks/usePHIProcessing'
import { useDrawingMode } from './hooks/useDrawingMode'
import { usePHIBoxRendering } from './hooks/usePHIBoxRendering'
import { useDataProcessing } from './hooks/useDataProcessing'

// Components
import { StatusAlert } from './components/StatusAlert'
import { LoadingOverlay } from './components/LoadingOverlay'
import { SeriesInfo } from './components/SeriesInfo'
import { ToolBar } from './components/ToolBar'
import { EditingControls } from './components/EditingControls'
import { EmptyState } from './components/EmptyState'
import StudiesAndSeriesGroup from '@features/subject/components/StudiesAndSeriesGroup'

// Utils and Types
import { VIEWPORT_CONFIG } from '../../constants/phi'
import type { PHIPrediction, PHIRedactionStepProps, PHIContainer } from '../../types/phi'
import type { StudyModel } from '@common/config/api/client'

const { ToolGroupManager, StackScrollTool, Enums: csToolsEnums } = cornerstoneTools

// Add proper types for selected series and cache
type SelectedSeries = {
  series: string
  study: string
  exam_metadata_id: string
  series_metadata_id: string
}

type SeriesCache = {
  imageIds: string[]
  urlToMetadataMap: Record<string, InstanceMetadata>
}

type InstanceMetadata = {
  SOPInstanceUID: string
  [key: string]: string | number | string[] | number[] | null
}

function PHIRedactionStepInner({ studyId, subjectId, timepointSubmissionId }: PHIRedactionStepProps) {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const elementRef = useRef<HTMLDivElement>(null)
  const updatePHIRedactionBoxes = useUpdatePHIRedactionBoxes()

  const { state, dispatch } = usePHIRedactionContext()

  // Cleanup queries on unmount
  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['TimePointSubmissionDetails', timepointSubmissionId],
      })
      queryClient.removeQueries({
        queryKey: ['PHIRedactionStatus'],
      })
    }
  }, [timepointSubmissionId, queryClient])

  // Invalidate queries when timepointSubmissionId changes
  useEffect(() => {
    if (timepointSubmissionId) {
      void queryClient.invalidateQueries({
        queryKey: ['TimePointSubmissionDetails', timepointSubmissionId],
      })
      void queryClient.invalidateQueries({
        queryKey: ['PHIRedactionStatus'],
      })
    }
  }, [timepointSubmissionId, queryClient])

  // API queries
  const { data: timePointSubmissionData, isLoading: isTimePointSubmissionLoading } = useGetTimePointSubmissionDetails(
    ['TimePointSubmissionDetails', timepointSubmissionId],
    {
      study_id: studyId,
      subject_id: subjectId,
      timepoint_submission_id: timepointSubmissionId,
    },
    {
      get_dicom_json: true,
    },
  )

  const { data: phiData, isLoading: isPhiStatusLoading } = useGetPHIRedactionStatus(
    {
      study_id: studyId,
      subject_id: subjectId,
      timepoint_submission_id: timepointSubmissionId,
    },
    {
      container_submission_ids:
        timePointSubmissionData?.data?.timepoint_submission.container_submissions.map(
          (x) => x.container_submission_id,
        ) || [],
    },
  )

  // Memoized callback functions to prevent infinite re-renders
  const onSetPHIPredictions = useCallback(
    (predictions: Record<string, PHIPrediction[]>) => {
      dispatch({ type: 'SET_PHI_PREDICTIONS', payload: predictions })
    },
    [dispatch],
  )

  const onSetPhiWorkflowState = useCallback(
    (inProgress: boolean, conversionInProgress: boolean, status: string) => {
      dispatch({ type: 'SET_PHI_WORKFLOW_IN_PROGRESS', payload: inProgress })
      dispatch({ type: 'SET_CONVERSION_IN_PROGRESS', payload: conversionInProgress })
      dispatch({ type: 'SET_PHI_WORKFLOW_STATUS', payload: status })
    },
    [dispatch],
  )

  const onSetDataToRender = useCallback(
    (data: Record<string, StudyModel>) => {
      dispatch({ type: 'SET_DATA_TO_RENDER', payload: data })
    },
    [dispatch],
  )

  const onSetSelectedSeries = useCallback(
    (series: SelectedSeries) => {
      dispatch({ type: 'SET_SELECTED_SERIES', payload: series })
    },
    [dispatch],
  )

  const onUpdateSeriesCache = useCallback(
    (seriesId: string, cache: SeriesCache) => {
      dispatch({ type: 'UPDATE_SERIES_CACHE', payload: { seriesId, cache } })
    },
    [dispatch],
  )

  const onUpdateUrlToMetadataMap = useCallback(
    (urlToMetadataMap: Record<string, InstanceMetadata>) => {
      dispatch({ type: 'SET_URL_TO_METADATA_MAP', payload: urlToMetadataMap })
    },
    [dispatch],
  )

  const onUpdateLoadingProgress = useCallback(
    (loaded: number, total: number, loadedImages: number) => {
      dispatch({ type: 'SET_IMAGES_LOADED_COUNT', payload: loaded })
      dispatch({ type: 'SET_TOTAL_IMAGES_TO_LOAD', payload: total })
      dispatch({ type: 'SET_LOADED_IMAGE_COUNT', payload: loadedImages })
    },
    [dispatch],
  )

  const onAllImagesLoaded = useCallback(
    (isLoaded: boolean) => {
      dispatch({ type: 'SET_ALL_IMAGES_LOADED', payload: isLoaded })
    },
    [dispatch],
  )

  const onCurrentImageChange = useCallback(
    (imageId: string | null) => {
      dispatch({ type: 'SET_CURRENT_IMAGE_ID', payload: imageId })
    },
    [dispatch],
  )

  const onLoading = useCallback(
    (isLoading: boolean) => {
      dispatch({ type: 'SET_LOADING', payload: isLoading })
    },
    [dispatch],
  )

  const onAddPHIPrediction = useCallback(
    (sopInstanceUid: string, prediction: PHIPrediction) => {
      dispatch({ type: 'ADD_PHI_PREDICTION', payload: { sopInstanceUid, prediction } })
    },
    [dispatch],
  )

  const onForceRedraw = useCallback(() => {
    dispatch({ type: 'SET_BOXES_DRAWN', payload: false })
  }, [dispatch])

  const onSetBoxesDrawn = useCallback(
    (drawn: boolean) => {
      dispatch({ type: 'SET_BOXES_DRAWN', payload: drawn })
    },
    [dispatch],
  )

  const onUpdatePrediction = useCallback(
    (sopInstanceUid: string, index: number, updates: Partial<PHIPrediction>) => {
      dispatch({ type: 'UPDATE_PHI_PREDICTION', payload: { sopInstanceUid, index, updates } })
    },
    [dispatch],
  )

  // Save PHI changes function (declared early to avoid hoisting issues)
  const savePhiChanges = useCallback(
    async (prediction: PHIPrediction, containerSubmissionId: string) => {
      const params = {
        study_id: studyId,
        subject_id: subjectId,
        timepoint_submission_id: timepointSubmissionId,
      }

      const data = {
        container_updates: [
          {
            timepoint_container_submission_id: containerSubmissionId,
            updates: [
              {
                exam_metadata_id: prediction.exam_metadata_id,
                series_metadata_id: prediction.series_metadata_id,
                study_instance_uid: prediction.study_instance_uid,
                series_instance_uid: prediction.series_instance_uid,
                new_bounding_boxes:
                  prediction.action === 'apply'
                    ? [
                        {
                          x: prediction.x,
                          y: prediction.y,
                          width: prediction.width,
                          height: prediction.height,
                          sop_instance_uid: prediction.sop_instance_uid,
                        },
                      ]
                    : [],
                redaction_ids_to_remove: prediction.action === 'remove' && prediction.id ? [prediction.id] : [],
                scope: prediction.scope || 'single',
              },
            ],
          },
        ],
      }

      await updatePHIRedactionBoxes.mutateAsync({ params, data })
    },
    [studyId, subjectId, timepointSubmissionId, updatePHIRedactionBoxes],
  )

  // Handle box selection function (declared early)
  const handleBoxSelect = useCallback(
    (sopInstanceUid: string, index: number) => {
      if (state.isDrawingMode) {
        dispatch({ type: 'TOGGLE_DRAWING_MODE' })
      }

      if (!state.isEditMode) {
        dispatch({ type: 'SET_EDIT_MODE', payload: true })
        const toolGroup = ToolGroupManager.getToolGroup('STACK_TOOL_GROUP_ID')
        if (toolGroup) {
          toolGroup.setToolPassive(StackScrollTool.toolName)
        }
      }

      dispatch({ type: 'SET_CURRENT_EDITING_BOX', payload: { sopInstanceUid, index } })
    },
    [state.isDrawingMode, state.isEditMode, dispatch],
  )

  // Initialize hooks with memoized callbacks
  const { getSeriesWithPhiDetected, getSeriesPHIStatus, getStatusMessage } = usePHIProcessing({
    phiData: phiData as unknown as { containers?: PHIContainer[] } | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timePointSubmissionData: timePointSubmissionData as any,
    timepointSubmissionId,
    onSetPHIPredictions,
    onSetPhiWorkflowState,
  })

  useDataProcessing({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timePointSubmissionData: timePointSubmissionData as any,
    isTimePointSubmissionLoading,
    phiData: phiData as unknown as { containers?: PHIContainer[] } | undefined,
    selectedSeries: state.selectedSeries,
    onSetDataToRender,
    onSetSelectedSeries,
  })

  const { engine, isInitialized } = useImageViewer({
    elementRef,
    selectedSeries: state.selectedSeries.series,
    dataToRender: state.dataToRender,
    seriesCache: state.seriesCache,
    onUpdateSeriesCache,
    onUpdateUrlToMetadataMap,
    onUpdateLoadingProgress,
    onAllImagesLoaded,
    onCurrentImageChange,
    onLoading,
    onForceRedraw,
  })

  useDrawingMode({
    isDrawingMode: state.isDrawingMode,
    elementRef,
    engine,
    currentImageId: state.currentImageId,
    urlToMetadataMap: state.urlToMetadataMap,
    selectedSeries: state.selectedSeries,
    phiData: phiData as unknown as { containers?: PHIContainer[] } | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timePointSubmissionData: timePointSubmissionData as any,
    onAddPHIPrediction,
    onSavePhiChanges: savePhiChanges,
    onForceRedraw,
  })

  usePHIBoxRendering({
    currentImageId: state.currentImageId,
    elementRef,
    engine,
    phiPredictions: state.phiPredictions,
    urlToMetadataMap: state.urlToMetadataMap,
    redactedSeries: state.redactedSeries,
    redactedSOPInstances: state.redactedSOPInstances,
    selectedSeries: state.selectedSeries.series,
    isEditMode: state.isEditMode,
    showRedactions: state.showRedactions,
    currentEditingBox: state.currentEditingBox,
    onSetBoxesDrawn,
    onBoxSelect: handleBoxSelect,
    onUpdatePrediction,
  })

  // Helper function to check if series has PHI predictions
  const seriesHasDrawablePredictions = (seriesUID: string): boolean => {
    if (!seriesUID || state.redactedSeries.has(seriesUID)) {
      return false
    }

    const redactedSOPs = state.redactedSOPInstances[seriesUID] || new Set<string>()

    return Object.entries(state.phiPredictions).some(
      ([sopInstanceUID, predictions]) =>
        !redactedSOPs.has(sopInstanceUID) &&
        predictions.some((prediction) => prediction.series_instance_uid === seriesUID),
    )
  }

  // Auto-show redactions when switching to series with PHI predictions
  useEffect(() => {
    if (state.selectedSeries.series) {
      const currentSeriesUID = state.selectedSeries.series
      const seriesHasAnyDrawablePreds = seriesHasDrawablePredictions(currentSeriesUID)

      if (seriesHasAnyDrawablePreds) {
        console.log(`Series ${currentSeriesUID} has PHI predictions, ensuring redactions are shown`)
        dispatch({ type: 'SET_SHOW_REDACTIONS', payload: true })
      }
    }
    // seriesHasDrawablePredictions reads only from state already listed below;
    // it is excluded to avoid re-running on every render (non-memoized inline fn).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedSeries.series, state.phiPredictions, state.redactedSeries, state.redactedSOPInstances, dispatch])

  // Auto-show redactions when PHI data loads for already selected series
  useEffect(() => {
    if (state.selectedSeries.series && Object.keys(state.phiPredictions).length > 0) {
      const currentSeriesUID = state.selectedSeries.series
      const seriesHasAnyDrawablePreds = seriesHasDrawablePredictions(currentSeriesUID)

      if (seriesHasAnyDrawablePreds) {
        console.log(`PHI data loaded for series ${currentSeriesUID}, ensuring redactions are shown`)
        dispatch({ type: 'SET_SHOW_REDACTIONS', payload: true })
      }
    }
    // seriesHasDrawablePredictions reads only from state already listed below;
    // it is excluded to avoid re-running on every render (non-memoized inline fn).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phiPredictions, state.selectedSeries.series, state.redactedSeries, state.redactedSOPInstances, dispatch])

  // Helper functions
  const getCurrentSeries = () => {
    if (!state.selectedSeries.study || !state.selectedSeries.series) return null
    return (
      state.dataToRender[state.selectedSeries.study]?.series.find(
        (x) => x.SeriesInstanceUID === state.selectedSeries.series,
      ) || null
    )
  }

  const getCurrentPrediction = (): PHIPrediction | null => {
    if (!state.currentEditingBox) return null
    const { sopInstanceUid, index } = state.currentEditingBox
    return state.phiPredictions[sopInstanceUid]?.[index] || null
  }

  const doesCurrentSeriesHaveMasks = (): boolean => {
    if (!state.selectedSeries.series || !phiData?.containers) return false
    if (state.redactedSeries.has(state.selectedSeries.series)) return false

    const seriesInstanceUID = state.selectedSeries.series
    const redactedSOPs = state.redactedSOPInstances[seriesInstanceUID] || new Set<string>()

    return Object.entries(state.phiPredictions).some(
      ([sopInstanceUID, predictions]) =>
        !redactedSOPs.has(sopInstanceUID) &&
        predictions.some((prediction) => prediction.series_instance_uid === seriesInstanceUID),
    )
  }

  const hasSeriesPHI = (seriesInstanceUID: string): boolean => {
    if (state.redactedSeries.has(seriesInstanceUID)) return false

    const redactedSOPs = state.redactedSOPInstances[seriesInstanceUID] || new Set<string>()
    const hasPHIPredictions = Object.entries(state.phiPredictions).some(
      ([sopInstanceUID, predictions]) =>
        !redactedSOPs.has(sopInstanceUID) &&
        predictions.some((prediction) => prediction.series_instance_uid === seriesInstanceUID),
    )

    if (!hasPHIPredictions) return false
    return getSeriesPHIStatus(seriesInstanceUID) === 'phi_detected'
  }

  // Event handlers
  const toggleDrawingMode = () => {
    const newDrawingMode = !state.isDrawingMode
    dispatch({ type: 'TOGGLE_DRAWING_MODE' })

    console.log('Toggling drawing mode:', {
      oldMode: state.isDrawingMode,
      newMode: newDrawingMode,
    })

    // Clear overlays and disable/enable tools based on NEW state
    const toolGroup = ToolGroupManager.getToolGroup('STACK_TOOL_GROUP_ID')
    if (!toolGroup) return

    if (newDrawingMode) {
      // Entering drawing mode - disable stack scroll
      toolGroup.setToolPassive(StackScrollTool.toolName)
      console.log('Entered drawing mode - disabled stack scroll')
    } else {
      // Exiting drawing mode - re-enable stack scroll with both Primary and Wheel bindings
      toolGroup.setToolActive(StackScrollTool.toolName, {
        bindings: [
          { mouseButton: csToolsEnums.MouseBindings.Primary },
          { mouseButton: csToolsEnums.MouseBindings.Wheel },
        ],
      })
      console.log('Exited drawing mode - enabled stack scroll')
    }
  }

  const toggleEditMode = (_event: React.MouseEvent<HTMLElement>, value: boolean) => {
    if (value === state.isEditMode) return

    const newEditMode = !state.isEditMode
    dispatch({ type: 'SET_EDIT_MODE', payload: newEditMode })

    console.log('Toggling edit mode:', {
      oldMode: state.isEditMode,
      newMode: newEditMode,
    })

    const toolGroup = ToolGroupManager.getToolGroup('STACK_TOOL_GROUP_ID')
    if (!toolGroup) return

    if (newEditMode) {
      // Entering edit mode - disable stack scroll
      toolGroup.setToolPassive(StackScrollTool.toolName)
      console.log('Entered edit mode - disabled stack scroll')
    } else {
      // Exiting edit mode - clear editing box and re-enable stack scroll with both Primary and Wheel bindings
      dispatch({ type: 'SET_CURRENT_EDITING_BOX', payload: null })
      toolGroup.setToolActive(StackScrollTool.toolName, {
        bindings: [
          { mouseButton: csToolsEnums.MouseBindings.Primary },
          { mouseButton: csToolsEnums.MouseBindings.Wheel },
        ],
      })
      console.log('Exited edit mode - enabled stack scroll')
    }
  }

  const cancelEditing = () => {
    dispatch({ type: 'SET_CURRENT_EDITING_BOX', payload: null })
  }

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!state.currentEditingBox) return
    const { sopInstanceUid, index } = state.currentEditingBox
    dispatch({
      type: 'UPDATE_PHI_PREDICTION',
      payload: {
        sopInstanceUid,
        index,
        updates: { action: event.target.value as 'apply' | 'remove' },
      },
    })
  }

  const handleScopeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!state.currentEditingBox) return
    const { sopInstanceUid, index } = state.currentEditingBox
    dispatch({
      type: 'UPDATE_PHI_PREDICTION',
      payload: {
        sopInstanceUid,
        index,
        updates: { scope: event.target.value as 'single' | 'all' },
      },
    })
  }

  const saveCurrentEdits = async () => {
    const prediction = getCurrentPrediction()
    if (!prediction) return

    const action = prediction.action || 'apply'
    const scope = prediction.scope || 'single'

    await applyChangesToSeries(prediction, action, scope)
  }

  // Apply changes to series function - Full implementation with Scope = All support
  const applyChangesToSeries = async (
    prediction: PHIPrediction,
    action: 'apply' | 'remove',
    scope: 'single' | 'all',
  ) => {
    if (!studyId || !subjectId || !timepointSubmissionId) {
      console.error('Missing required IDs for applying changes')
      return
    }
    console.log('Applying changes to series:', prediction, action, scope)

    const params = {
      study_id: studyId,
      subject_id: subjectId,
      timepoint_submission_id: timepointSubmissionId,
    }

    const containerSubmissions = timePointSubmissionData?.data?.timepoint_submission.container_submissions || []
    const containerSubmission = containerSubmissions.find(
      (cs) => cs.container_submission_id && prediction.exam_metadata_id,
    )

    if (!containerSubmission) {
      console.error('Container submission not found')
      return
    }

    try {
      // Get all SOP instance UIDs for the current series if scope is 'all'
      let allSopInstanceUids: string[] = []
      if (scope === 'all') {
        // Use cached data if available, otherwise fetch from DICOM JSON
        const cachedData = state.seriesCache[prediction.series_instance_uid]
        if (cachedData) {
          allSopInstanceUids = Object.values(cachedData.urlToMetadataMap).map((meta) => meta.SOPInstanceUID)
          console.log('All SOP instance UIDs from cache:', allSopInstanceUids)
        } else {
          // Fallback to DICOM JSON data - use proper typing for studies
          type StudyWithSeries = {
            StudyInstanceUID: string
            series: Array<{
              SeriesInstanceUID: string
              instances: Array<{
                metadata: { SOPInstanceUID: string }
              }>
            }>
          }

          const studies = containerSubmission.dicom_json?.studies as StudyWithSeries[] | undefined
          const study = studies?.find((s) => s.StudyInstanceUID === prediction.study_instance_uid)
          const series = study?.series.find((s) => s.SeriesInstanceUID === prediction.series_instance_uid)
          if (series?.instances) {
            allSopInstanceUids = series.instances.map((instance) => instance.metadata.SOPInstanceUID)
            console.log('All SOP instance UIDs from DICOM JSON:', allSopInstanceUids)
          }
        }
      }

      // Prepare redaction IDs to remove
      const redactionIdsToRemove: string[] = []
      if (action === 'remove') {
        if (scope === 'single' && prediction.id) {
          redactionIdsToRemove.push(prediction.id)
        } else if (scope === 'all') {
          // Find all matching predictions across all instances
          if (allSopInstanceUids.length > 0) {
            allSopInstanceUids.forEach((sopInstanceUid) => {
              const predictions = state.phiPredictions[sopInstanceUid] || []
              predictions.forEach((p) => {
                // Match based on coordinates and other properties
                if (
                  p.x === prediction.x &&
                  p.y === prediction.y &&
                  p.width === prediction.width &&
                  p.height === prediction.height &&
                  p.id
                ) {
                  redactionIdsToRemove.push(p.id)
                }
              })
            })
          }
        }
      }

      // Prepare new bounding boxes for apply action
      const newBoundingBoxes =
        action === 'apply'
          ? scope === 'all'
            ? allSopInstanceUids
                .filter(
                  (sopInstanceUid) =>
                    // Only include SOPs that are not the current one if we're editing
                    !prediction.id || sopInstanceUid !== prediction.sop_instance_uid,
                )
                .map((sopInstanceUid) => ({
                  x: prediction.x,
                  y: prediction.y,
                  width: prediction.width,
                  height: prediction.height,
                  sop_instance_uid: sopInstanceUid,
                }))
            : [
                {
                  x: prediction.x,
                  y: prediction.y,
                  width: prediction.width,
                  height: prediction.height,
                  sop_instance_uid: prediction.sop_instance_uid,
                },
              ]
          : []

      // Prepare edits - only for single scope edits with existing ID
      const edits =
        action === 'apply' && prediction.id && scope === 'single'
          ? [
              {
                id: prediction.id,
                x: prediction.x,
                y: prediction.y,
                width: prediction.width,
                height: prediction.height,
                sop_instance_uid: prediction.sop_instance_uid,
              },
            ]
          : []

      const data = {
        container_updates: [
          {
            timepoint_container_submission_id: containerSubmission.container_submission_id,
            updates: [
              {
                exam_metadata_id: prediction.exam_metadata_id,
                series_metadata_id: prediction.series_metadata_id,
                study_instance_uid: prediction.study_instance_uid,
                series_instance_uid: prediction.series_instance_uid,
                new_bounding_boxes: newBoundingBoxes,
                edits: edits,
                redaction_ids_to_remove: redactionIdsToRemove,
                scope: scope,
              },
            ],
          },
        ],
      }

      await updatePHIRedactionBoxes.mutateAsync({ params, data })

      // Update local state based on action and scope
      if (action === 'apply') {
        if (scope === 'all') {
          // Add the redaction to all instances in the series
          allSopInstanceUids.forEach((sopInstanceUid) => {
            if (prediction.id) {
              // Update existing prediction
              dispatch({
                type: 'UPDATE_PHI_PREDICTION',
                payload: {
                  sopInstanceUid,
                  index: state.phiPredictions[sopInstanceUid]?.findIndex((p) => p.id === prediction.id) || 0,
                  updates: { ...prediction, action, scope },
                },
              })
            } else {
              // Add new prediction to each SOP instance
              dispatch({
                type: 'ADD_PHI_PREDICTION',
                payload: {
                  sopInstanceUid,
                  prediction: { ...prediction, action, scope, sop_instance_uid: sopInstanceUid },
                },
              })
            }
          })
        } else {
          // Single scope update
          if (prediction.id) {
            // Update existing prediction
            const index =
              state.phiPredictions[prediction.sop_instance_uid]?.findIndex((p) => p.id === prediction.id) || 0
            dispatch({
              type: 'UPDATE_PHI_PREDICTION',
              payload: {
                sopInstanceUid: prediction.sop_instance_uid,
                index,
                updates: { ...prediction, action, scope },
              },
            })
          } else {
            // Add new prediction
            dispatch({
              type: 'ADD_PHI_PREDICTION',
              payload: {
                sopInstanceUid: prediction.sop_instance_uid,
                prediction: { ...prediction, action, scope },
              },
            })
          }
        }
      } else if (action === 'remove') {
        if (scope === 'all') {
          // Remove all matching predictions across all instances
          Object.keys(state.phiPredictions).forEach((sopInstanceUid) => {
            const predictions = state.phiPredictions[sopInstanceUid] || []
            predictions.forEach((p) => {
              if (redactionIdsToRemove.includes(p.id!)) {
                // Note: This would need a new action type for removing by ID
                console.log(`Would remove prediction ${p.id} from ${sopInstanceUid}`)
              }
            })
          })
        } else {
          // Remove single prediction - would need a REMOVE_PHI_PREDICTION action
          console.log(`Would remove prediction ${prediction.id} from ${prediction.sop_instance_uid}`)
        }
      }

      // Clear the editing state after successful save
      cancelEditing()
    } catch (error) {
      console.error('Failed to apply changes:', error)
      throw error
    }
  }

  const currentSeries = getCurrentSeries()

  return (
    <Box px={8} pb={5}>
      <Typography color="text.primary" variant="h4">
        DICOM files PHI redaction
      </Typography>
      <Typography color="text.secondary" variant="body2" mb={4}>
        AI will analyze images in all series to detect PHI. Review the series labeled "PHI suspected" and confirm the
        PHI redaction.
      </Typography>

      <StatusAlert
        phiWorkflowInProgress={state.phiWorkflowInProgress}
        phiWorkflowStatus={state.phiWorkflowStatus}
        isPhiStatusLoading={isPhiStatusLoading}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        timePointSubmissionData={timePointSubmissionData as any}
        getStatusMessage={getStatusMessage}
      />

      <Grid container spacing={2}>
        <Grid size={8}>
          <Box bgcolor={theme.palette.background.paper} borderRadius={4} p={4}>
            {isTimePointSubmissionLoading || !isInitialized ? null : (
              <>
                <SeriesInfo
                  seriesToProcess={currentSeries}
                  studyDate={state.dataToRender[state.selectedSeries.study]?.StudyDate || ''}
                  hasSeriesPHI={hasSeriesPHI(state.selectedSeries.series)}
                  isDrawingMode={state.isDrawingMode}
                />

                <Box mb={2}>
                  <ToolBar
                    isDrawingMode={state.isDrawingMode}
                    isEditMode={state.isEditMode}
                    showRedactions={state.showRedactions}
                    allImagesLoaded={state.allImagesLoaded}
                    loadedImageCount={state.loadedImageCount}
                    totalImagesInSeries={state.totalImagesInSeries}
                    hasCurrentSeriesMasks={doesCurrentSeriesHaveMasks()}
                    onToggleDrawingMode={toggleDrawingMode}
                    onToggleEditMode={toggleEditMode}
                    onToggleShowRedactions={() =>
                      dispatch({ type: 'SET_SHOW_REDACTIONS', payload: !state.showRedactions })
                    }
                  />

                  <EditingControls
                    isEditMode={state.isEditMode}
                    currentEditingBox={state.currentEditingBox}
                    currentPrediction={getCurrentPrediction()}
                    onActionChange={handleActionChange}
                    onScopeChange={handleScopeChange}
                    onSaveCurrentEdits={saveCurrentEdits}
                    onCancelEditing={cancelEditing}
                  />
                </Box>
              </>
            )}

            <Box position="relative">
              <div
                ref={elementRef}
                tabIndex={0}
                style={{
                  display: 'flex',
                  height: VIEWPORT_CONFIG.height,
                  minHeight: VIEWPORT_CONFIG.minHeight,
                  maxHeight: VIEWPORT_CONFIG.maxHeight,
                  backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#000',
                  outline: 'none',
                }}
              />
              <LoadingOverlay
                isLoading={state.isLoading}
                conversionInProgress={state.conversionInProgress && !Object.keys(state.dataToRender).length}
                totalImagesToLoad={state.totalImagesToLoad}
                imagesLoadedCount={state.imagesLoadedCount}
                message="Converting files to DICOM format..."
              />
            </Box>
          </Box>
        </Grid>

        <Grid size={4} px={4}>
          <Typography variant="h6" mb={4}>
            Studies and series
          </Typography>

          <EmptyState
            conversionInProgress={state.conversionInProgress && Object.keys(state.dataToRender).length === 0}
          />

          {Object.keys(state.dataToRender).map((studyUID, index) => {
            const seriesWithPhiDetected = getSeriesWithPhiDetected(
              phiData as unknown as { containers?: PHIContainer[] } | undefined,
              studyUID,
              state.dataToRender,
            )
            const filteredSeriesWithPhiDetected = seriesWithPhiDetected.filter(
              (seriesUID) => !state.redactedSeries.has(seriesUID),
            )
            const hasStudyPhiDetected = filteredSeriesWithPhiDetected.length > 0

            return (
              <StudiesAndSeriesGroup
                key={index}
                name={state.dataToRender[studyUID].StudyDescription}
                children={state.dataToRender[studyUID].series}
                selectedSeries={state.selectedSeries.series}
                onSeriesClick={(series) => {
                  // Find metadata IDs from PHI data
                  let examMetadataId = ''
                  let seriesMetadataId = ''

                  if (phiData?.containers) {
                    for (const container of phiData.containers) {
                      const matchingExam = container.exams.find((exam) => exam.study_instance_uid === studyUID)
                      if (matchingExam) {
                        const matchingSeries = matchingExam.series.find(
                          (seriesItem) => seriesItem.series_instance_uid === series,
                        )
                        if (matchingSeries) {
                          examMetadataId = matchingExam.exam_metadata_id
                          seriesMetadataId = matchingSeries.series_metadata_id
                          break
                        }
                      }
                    }
                  }

                  dispatch({
                    type: 'SET_SELECTED_SERIES',
                    payload: {
                      series,
                      study: studyUID,
                      exam_metadata_id: examMetadataId,
                      series_metadata_id: seriesMetadataId,
                    },
                  })

                  // Show redactions if series has PHI predictions
                  if (
                    Object.entries(state.phiPredictions).some(([, predictions]) =>
                      predictions.some((p) => p.series_instance_uid === series),
                    )
                  ) {
                    dispatch({ type: 'SET_SHOW_REDACTIONS', payload: true })
                  }
                }}
                hasPhiDetected={hasStudyPhiDetected}
                seriesWithPhiDetected={filteredSeriesWithPhiDetected}
                isPhiLoading={state.phiWorkflowInProgress || isPhiStatusLoading}
              />
            )
          })}
        </Grid>
      </Grid>
    </Box>
  )
}

// Main component with context provider
export default function PHIRedactionStepRefactored(props: PHIRedactionStepProps) {
  return (
    <PHIRedactionProvider>
      <PHIRedactionStepInner {...props} />
    </PHIRedactionProvider>
  )
}
